import { useEffect, useState } from 'react';
import { useArticlesListQuery } from './articlesList.generated';
import { Article, LocationStatus } from '@/types';

interface ArticlesPaginationProps {
  retailerId: string;
  categoryId: string | null;
  searchTerm?: string;
  perPage: number;
}

const useArticlesPagination = (variables: ArticlesPaginationProps) => {
  const { retailerId, categoryId, searchTerm = '', perPage } = variables;
  const [after, setAfter] = useState('1');
  const [cursors, setCursors] = useState<(string | undefined)[]>([]);
  const [inventoryResult, setInventoryResult] = useState<Article[]>([]);

  const [{ data, error, fetching }] = useArticlesListQuery({
    variables: {
      retailerId,
      after,
      first: perPage,
      categoryId,
      searchTerm,
      locationStatus: LocationStatus.Active,
    },
  });

  useEffect(() => {
    // Reset state on category change
    setAfter('1');
    setCursors([]);
    setInventoryResult([]);
  }, [categoryId, searchTerm]);

  const hasNextPage = data?.articlesConnection?.edges?.some(
    (edge) => edge.nextCursor !== null,
  );
  const hasPreviousPage = cursors.length > 0;

  const endCrusor = data?.articlesConnection?.pageInfo?.endCursor;

  const loadMore = () => {
    if (!hasNextPage) return;
    const newCursors = [...cursors, endCrusor];
    setCursors(newCursors || []);
    setAfter(endCrusor ? endCrusor + 1 : '0');
  };

  useEffect(() => {
    if (data?.articlesConnection?.edges) {
      const newInventoryResult = data.articlesConnection.edges.map(
        (edge) => edge.node,
      );
      setInventoryResult(
        (prevState) => [...prevState, ...newInventoryResult] as Article[],
      );
    }
  }, [data]);

  return {
    inventoryResult,
    fetching,
    error,
    loadMore,
    hasNextPage,
    hasPreviousPage,
    cursors,
  };
};

export default useArticlesPagination;
